<template>
<section class="map-player-control-bar-wrapper">
  <section ref="map-ele-wrapper" class="map-ele-wrapper">
    <slot />

    <div v-show="isRecordScreen" class="record-screen-title">{{recordScreenTitle}}</div>
    <div v-show="isRecordScreen" class="record-screen-data-time">{{currentTimeStamp === 0 ? '--' : new Date(currentTimeStamp).format()}}</div>
  </section>

  <section class="player-control-bar-wrapper">
    <section class="player-control-bar">
      <section class="player-control-bar-inner">
        <div class="play-control-data-time">{{currentTimeStamp === 0 ? '--' : new Date(currentTimeStamp).format()}}</div>

        <div class="player-status-bar">
          <div
            class="play-control-btn"
            @click="handleChangePlayStatus"
          >
            <i :class="isPlay ? 'el-icon-video-pause' : 'el-icon-caret-right'" />
          </div>

          <div
            title="导出gif"
            :class="{'is-record-screen': isRecordScreen}"
            class="play-control-btn record-screen"
            @click="onRecordScreenBtnClick"
          >
            <i class="record-screen-icon"></i>
          </div>

          <div
            :class="{'disabled': times === oTimes / 4}"
            style="font-size:20px;margin-left:1em;"
            class="play-control-btn"
            @click="handleChangeTimes(oTimes / 4, 0.5, 0)"
          >
            <i class="el-icon-plus" />
          </div>

          <div
            :class="{'disabled' : times === oTimes * 4}"
            style="font-size:20px;margin-left:.5em;"
            class="play-control-btn"
            @click="handleChangeTimes(oTimes * 4, 2, 1)"
          >
            <i class="el-icon-minus" />
          </div>

          <span style="font-size:14px;padding-left:.5em;color:#FFF;">{{currentTimes}}</span>
        </div>
      </section>
      <section ref="progressWrapper" :class="{dragging: isDragging}" class="player-progress-bar" @click="handleChangeProgress">
        <div class="player-progress-bar-container">
          <div ref="progress" :style="{width:`${progress}%`}" class="player-progress-bar-container-inner">
            <div class="player-progress-bar-thumb" @mousedown="handleBeginDrag"></div>
          </div>
        </div>
      </section>
    </section>
  </section>
</section>
</template>

<script>
import GIF from 'gif.js'
import html2canvas from 'html2canvas'

import eventKeyMap from './eventKeyMap'
import BaseStore from '../../BaseStore'

const MAP_PLAYER_DATA_CHANGE = 'MapPlayerDataChanged'

const O_TIMES = 0.1
const GIF_SIZE_RATIO = 0.75

export default {
  name: 'MapPlayerCtrlBar',
  extends: BaseStore,
  props: {
    recordScreenTitle: {
      type: String
    }
  },
  data () {
    return {
      isPlay: false,
      isRecordScreen: false,
      progress: 0,
      isDragging: false,
      beginTimeStamp: 0,
      endTimeStamp: 0,
      currentTimeStamp: 0,
      times: O_TIMES
    }
  },
  computed: {
    duration () {
      return this.endTimeStamp - this.beginTimeStamp
    },
    playStep () {
      return 1000 * this.times
    },
    oTimes: () => O_TIMES,
    currentTimes () {
      let {times, oTimes} = this
      return times === oTimes ? '正常倍速' : `${oTimes / times}倍速`
    }
  },
  methods: {
    handleReset () {
      this.resetPlayStatus()
      this.resetRecordStatus()
    },
    resetPlayStatus () {
      this.clearPlayTimer()
      this.isPlay = false
      this.progress = 0
      this.dataIndex = -1
    },
    resetRecordStatus () {
      this.clearRecordScreenTimer()
      this.isRecordScreen = false
    },
    handleMapDataChanged (data) {
      this.handleReset()

      let tsList = data.tsList
      let dataClearKey = data.dataClearKey

      if (!(tsList instanceof Array) || tsList.length === 0) {
        this.currentTimeStamp = 0
        this.endTimeStamp = 0
        this.dataList = this.tsList = null
        this.getEventBus().emit(dataClearKey)
        return
      }

      let tsLen = tsList.length
      let lngLatChangeKey = this.lngLatChangeKey = data.lngLatChangeKey
      let dataList = this.dataList = data.dataList
      this.dataChangeKey = data.dataChangeKey
      this.dataClearKey = dataClearKey
      this.tsList = tsList

      this.currentTimeStamp = this.beginTimeStamp = tsList[0]
      this.endTimeStamp = tsList[tsLen - 1]

      this.getEventBus().emit(lngLatChangeKey, dataList[0])
    },
    handleBeginDrag (event) {
      if (this.duration === 0 || !this.dataList) {
        return
      }

      this.x = event.clientX
      this.progressBarWidth = this.$refs.progress.offsetWidth
      this.isDragging = true

      document.body.addEventListener('mousemove', this.handleDrag, true)
      document.body.addEventListener('mouseup', this.handleEndDrag, true)
    },
    handleDrag (event) {
      let x = event.clientX
      let diff = x - this.x
      let width = this.progressBarWidth + diff
      let progressWrapperWidth = this.$refs.progressWrapper.offsetWidth
      let ratio = Math.max(0, width / progressWrapperWidth)
      this.progress = ratio * 100
      this.dataIndex = Math.floor(this.dataList.length * ratio)
      this.changeData()
    },
    handleEndDrag () {
      document.body.removeEventListener('mousemove', this.handleDrag, true)
      document.body.removeEventListener('mouseup', this.handleEndDrag, true)
      this.isDragging = false
    },
    handleChangeProgress (event) {
      if (!this.dataList) {
        return
      }

      let x = event.layerX
      let progressWrapperWidth = this.$refs.progressWrapper.offsetWidth
      let ratio = x / progressWrapperWidth
      this.progress = ratio * 100
      this.dataIndex = Math.floor(this.dataList.length * ratio)
      this.changeData()
    },
    handleChangePlayStatus () {
      if (!this.tsList) {
        return
      }

      this.isPlay = !this.isPlay

      if (this.isPlay) {
        this.beginPlay()
      } else {
        this.clearPlayTimer()
      }
    },
    changeData () {
      let dataIndex = this.dataIndex
      let ts = this.tsList[dataIndex]
      let data = this.dataList.find(data => data.dataTimeStamp === ts)

      if (data) {
        this.currentTimeStamp = data.dataTimeStamp
        this.getEventBus().emit(this.dataChangeKey, data)
        return true
      }

      return false
    },
    beginPlay () {
      let tsLen = this.tsList.length - 1
      let dataIndex = this.dataIndex += 1
      this.progress = dataIndex / tsLen * 100
      this.playNext(tsLen, dataIndex)
    },
    playNext (tsLen, dataIndex) {
      if (this.changeData()) {
        if (dataIndex < tsLen) {
          this.playTimer = setTimeout(this.beginPlay, this.playStep)
        } else {
          this.isPlay = false
          this.dataIndex = -1
        }
      } else {
        this.playTimer = setTimeout(() => {
          this.playNext(tsLen, dataIndex)
        }, 500)
      }
    },
    handlePlayNextFrame (callback) {
      let flag = false
      let tsList = this.tsList

      if (tsList instanceof Array) {
        let tsLen = tsList.length - 1

        if (tsLen > 0) {
          let dataIndex = this.dataIndex += 1
          this.progress = dataIndex / tsLen * 100

          if (dataIndex <= tsLen) {
            flag = true
            this.changeData()
          } else {
            this.dataIndex = -1
          }
        }
      }

      if (typeof callback === 'function') {
        callback(flag, tsList)
      }
    },
    handleKeyup (event) {
      let eventTarget = event.target

      if (eventTarget === 'input') {
        return
      }

      if (event.keyCode === 65) {
        this.handleChangePlayStatus()
      }
    },
    clearPlayTimer () {
      clearTimeout(this.playTimer)
    },
    getEventBus () {
      return this.eventBus || this.$eventBus
    },
    handleChangeTimes (limit, n, index) {
      let limits = [this.times, limit]

      if (limits[index] <= limits[index ^ 1]) {
        return
      }

      this.times *= n
    },
    clearRecordScreenTimer () {
      clearTimeout(this.recordScreenTimer)
    },
    onRecordScreenBtnClick () {
      this.clearRecordScreenTimer()

      if (!this.tsList) {
        return
      }

      this.isRecordScreen = !this.isRecordScreen

      if (this.isRecordScreen) {
        this.$emit('record-screen')
        this.changeLoadStatus(true)

        this.recordScreenTimer = setTimeout(() => {
          let mapEleWrapper = this.$refs['map-ele-wrapper']
          this.gif = new GIF({
            workers: 2,
            quality: 15,
            width: mapEleWrapper.offsetWidth * GIF_SIZE_RATIO,
            height: mapEleWrapper.offsetHeight * GIF_SIZE_RATIO,
            workerScript: '/static/vendor/gifJS/gif.worker.js'
          })

          this.gif.on('finished', blob => {
            let filename = this.recordScreenTitle

            if (navigator.msSaveOrOpenBlob) {
              navigator.msSaveOrOpenBlob(blob, filename)
            } else {
              let url = URL.createObjectURL(blob)
              let link = document.createElement('a')
              link.download = filename
              link.href = url
              link.click()
              URL.revokeObjectURL(url)
            }

            this.changeLoadStatus(false)
            this.isRecordScreen = false
          })

          this.capNextFrame()
        }, 1000)
      } else {

      }
    },
    capNextFrame () {
      this.handlePlayNextFrame((flag, tsList) => {
        if (!(tsList instanceof Array) || tsList.length === 0) {
          this.changeLoadStatus(false)
          this.isRecordScreen = false
        } else if (flag) {
          let mapEleWrapper = this.$refs['map-ele-wrapper']

          html2canvas(mapEleWrapper, {
            logging: true,
            allowTaint: true,
            useCORS: true,
            scale: 1
          }).then(canvas => {
            let tmpCanvas = document.createElement('canvas')
            let tmpCtx = tmpCanvas.getContext('2d')
            let width = mapEleWrapper.offsetWidth * GIF_SIZE_RATIO
            let height = mapEleWrapper.offsetHeight * GIF_SIZE_RATIO

            tmpCanvas.width = width
            tmpCanvas.height = height

            tmpCtx.drawImage(
              canvas, // 规定要使用的图像、画布或视频。
              0, 0, // 开始剪切的 x 坐标位置。
              canvas.width, canvas.height, // 被剪切图像的高度。
              0, 0, // 在画布上放置图像的 x 、y坐标位置。
              width, height // 要使用的图像的宽度、高度
            )

            this.gif.addFrame(tmpCanvas, {delay: 100})
            this.capNextFrame()
          })
        } else {
          this.gif.render()
        }
      })
    }
  },
  created () {
    let eventBus = this.getEventBus()
    eventBus.on(eventKeyMap.RESET, this.handleReset)
    eventBus.on(eventKeyMap.NEXT_FRAME, this.handlePlayNextFrame)
    eventBus.on(MAP_PLAYER_DATA_CHANGE, this.handleMapDataChanged)
    document.addEventListener('keyup', this.handleKeyup, true)
  },
  beforeDestroy () {
    let eventBus = this.getEventBus()
    eventBus.off(eventKeyMap.RESET, this.handleReset)
    eventBus.off(eventKeyMap.NEXT_FRAME, this.handlePlayNextFrame)
    eventBus.off(MAP_PLAYER_DATA_CHANGE, this.handleMapDataChanged)
    document.removeEventListener('keyup', this.handleKeyup, true)
    this.handleReset()
  }
}
</script>

<style lang="scss" scoped>
.map-player-control-bar-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  > .map-ele-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;

    > .record-screen-title {
      position: absolute;
      font-size: 32px;
      top: 10px;
      left: 0;
      width: 100%;
      text-shadow: 2px 2px 2px rgba(0, 0, 0, .5);
      text-align: center;
      color: rgba(255, 255, 0, .9);
      user-select: none;
    }

    > .record-screen-data-time {
      position: absolute;
      font-size: 20px;
      bottom: 10px;
      left: 0;
      padding: 5px 16px;
      color: #ffffff;
      text-shadow: 1px 1px 1px rgba(0, 0, 0, .5);
      user-select: none;
    }
  }

  > .player-control-bar-wrapper {
    position: absolute;
    right: 0;
    bottom: 10px;
    left: 0;
    width: 70%;
    height: 40px;
    margin: auto;
    z-index: 3;

    .player-control-bar {
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, .5);

      .player-control-bar-inner {
        position: relative;
        width: 100%;
        height: 100%;
        padding: 5px 16px;
        background-image: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6));
      }

      .player-status-bar {
        user-select: none;
      }

      .play-control-btn {
        font-size: 30px;
        display: inline-block;
        width: 1em;
        height: 1em;
        line-height: 1;
        vertical-align: middle;
        color: #EDEDED;
        cursor: pointer;

        &:hover {
          color: #FFF;
        }

        &.disabled {
          opacity: .5;
        }

        &.record-screen {
          position: relative;

          > .record-screen-icon {
            position: absolute;
            display: block;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            width: 24px;
            height: 24px;
            margin: auto;
            border: 2px solid #ffffff {
              radius: 50%;
            };

            &:before {
              content: "";
              position: absolute;
              display: block;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              width: 60%;
              height: 60%;
              border-radius: 50%;
              margin: auto;
              background-color: #ffffff;
            }
          }

          &.is-record-screen {
            > .record-screen-icon {
              &:before {
                background-color: #F56C6C;
                animation: flash 2s infinite;
              }
            }
          }
        }
      }

      .play-control-data-time {
        font-size: 14px;
        float: right;
        margin-top: 7px;
        color: #FFF;
      }

      .player-progress-bar {
        position: absolute;
        top: -10px;
        left: 0;
        width: 100%;
        height: 10px;
        cursor: pointer;

        &.dragging,
        &:hover {
          .player-progress-bar-container {
            height: 10px;
            opacity: 1;
          }

          .player-progress-bar-thumb {
            opacity: 1;
          }
        }

        .player-progress-bar-container {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: rgba(133, 133, 133, 0.3);
          opacity: .85;
          transition: height .2s ease-in, opacity .2s ease-out;
        }

        .player-progress-bar-container-inner {
          position: absolute;
          max-width: 100%;
          height: 100%;
          background-image: linear-gradient(270deg, #0D9BFF 0%, #00D3FF 100%);
          background-color: #148AFF;
          transform: scaleX(-1);
        }

        .player-progress-bar-thumb {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 0;
          height: 0;
          margin: auto 0 auto -8px;
          border: 8px solid #00D3FF {
            radius: 50%;
          };
          opacity: 0;
          transition: opacity .2s ease-in;
          cursor: pointer;

          &:before {
            content: "";
            position: absolute;
            top: -15px;
            left: -15px;
            border: 15px solid rgba(0, 211, 255, .5) {
              radius: 15px;
            };
          }
        }
      }
    }
  }
}

@keyframes flash {
  0% {
    opacity: 1
  }
  50% {
    opacity: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MapTools/PlayerCtrlBar/index.vue